import { useContext, useEffect, useRef } from "react";

import { Logger } from "@/apis/Logger";
import { isVpnPreferenceSeedControlAvailable } from "@/helpers/electron-helper";
import { getOS, OPERATING_SYSTEM } from "@/helpers/os-helper";
import { AppContext } from "@/stores/appStore";
import {
  useFeatureFlag,
  useFeatureFlagsContext,
} from "@/stores/contexts/FeatureFlagsContext";

import type { VpnPreferenceVariant } from "../@types/vpn";

/**
 * The name AppConfig publishes as `d1-winVpnSkipPreferenceSeed`, minus the consumer prefix
 * the API strips. The `FeatureFlags` union ends in `(string & {})`, so a misspelling here
 * type-checks and reads as disabled.
 *
 * An absent flag reads the same as off, so a config version that predates it resolves to
 * `legacy` and re-pins a device already cleared. Flip it off, never delete it, until the
 * desktop app stops defaulting to `legacy`.
 */
const WIN_VPN_SKIP_PREFERENCE_SEED_FLAG = "winVpnSkipPreferenceSeed";

/**
 * Tell the desktop app which Cisco VPN preference to seed for this hire.
 *
 * Reported as soon as the flags settle, and again whenever the resolved variant changes.
 */
export const useVpnPreferenceSeed = (): void => {
  const { state } = useContext(AppContext);
  const { isLoading, isError } = useFeatureFlagsContext();
  const shouldSkipSeed = useFeatureFlag(WIN_VPN_SKIP_PREFERENCE_SEED_FLAG);
  const reportedVariant = useRef<VpnPreferenceVariant>();

  useEffect(() => {
    // Only Windows seeds a preference at all, so reporting elsewhere would ask the
    // desktop app to store a variant no launch would ever read.
    if (getOS() !== OPERATING_SYSTEM.WINDOWS) {
      return;
    }
    if (!isVpnPreferenceSeedControlAvailable()) {
      return;
    }
    // `isLoading` is false before sign-in, because react-query reports the disabled query
    // as idle; and reporting the default after a failed fetch would re-pin a cleared group.
    if (!state.amplifyConfigured || isLoading || isError) {
      return;
    }

    const variant: VpnPreferenceVariant = shouldSkipSeed ? "none" : "legacy";
    if (reportedVariant.current === variant) {
      return;
    }
    reportedVariant.current = variant;

    // Not retried, because the ref is already written: the device keeps whatever it last
    // recorded until something changes the resolved variant. A refusal is a contract break
    // rather than a failed write, so it is logged separately from a rejection.
    window.electronAPI
      ?.setVpnPreferenceVariant?.(variant)
      ?.then((accepted) =>
        accepted
          ? undefined
          : Logger.error(
              `The desktop app refused the "${variant}" VPN preference.`
            )
      )
      .catch((error) => {
        Logger.errorErrorLike(
          "Failed to report the VPN preference variant",
          error
        );
      });
  }, [state.amplifyConfigured, isLoading, isError, shouldSkipSeed]);
};
